import CustomButton from "@components/buttons/CustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { invoiceServices } from "@services/apis/Invoice";
import { transactionApiServices } from "@services/apis/Transactions";
import { AddExchangeRequest } from "@services/models/InvoiceApprovals/AddExchangeModel";
import { GetTokenListData } from "@services/models/Transactions/GetTokenList";
import { useEffect, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface ExchangeManagementSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onExchangeAdded?: () => void;
}

const ExchangeManagementSlider: React.FC<ExchangeManagementSliderProps> = ({
  isOpen,
  onClose,
  onExchangeAdded,
}) => {
  const [exchangeName, setExchangeName] = useState<string>("");
  const [cryptoAddress, setCryptoAddress] = useState<string>("");
  const [selectedToken, setSelectedToken] = useState<string>("");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [tokenId, setTokenId] = useState<number | undefined>();
  const [networkId, setNetworkId] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [tokenList, setTokenList] = useState<GetTokenListData[]>([]);
  const [tokenListLoading, setTokenListLoading] = useState<boolean>(false);

  const { handleError, handleSuccess } = useSnackbarMessage();

  const fetchTokenList = async () => {
    try {
      setTokenListLoading(true);
      const response = await transactionApiServices.getTokenList();
      setTokenList(response.data);
    } catch (error) {
      handleError({ message: "Failed to load token list" });
    } finally {
      setTokenListLoading(false);
    }
  };

  const handleTokenSelection = (value: string | number) => {
    const currencyId = Number(value);
    const token = tokenList.find((t) => t.currencyId === currencyId);

    if (token) {
      setTokenId(currencyId);
      setSelectedToken(token.currencySymbol);
      setNetworkId(undefined);
      setSelectedNetwork("");
    }
  };

  const handleNetworkSelection = (value: string | number) => {
    const networkId = Number(value);
    const token = tokenList.find((t) => t.currencyId === tokenId);
    const network = token?.networks?.find((n) => n.networkId === networkId);

    if (network) {
      setNetworkId(networkId);
      setSelectedNetwork(network.networkSymbol);
    }
  };

  const handleSubmit = async () => {
    if (!exchangeName.trim()) {
      handleError({ message: "Please enter exchange name" });
      return;
    }
    if (!selectedToken) {
      handleError({ message: "Please select a token" });
      return;
    }
    if (!selectedNetwork) {
      handleError({ message: "Please select a network" });
      return;
    }
    if (!cryptoAddress.trim()) {
      handleError({ message: "Please enter crypto address" });
      return;
    }

    try {
      setIsLoading(true);
      const request: AddExchangeRequest = {
        exchange: exchangeName.trim(),
        token: selectedToken,
        network: selectedNetwork,
        address: cryptoAddress.trim(),
      };

      await invoiceServices.addExchange(request);

      handleSuccess({ message: "Exchange added successfully" });

      setExchangeName("");
      setCryptoAddress("");
      setSelectedToken("");
      setSelectedNetwork("");
      setTokenId(undefined);
      setNetworkId(undefined);

      if (onExchangeAdded) {
        onExchangeAdded();
      }

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to add exchange";
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setExchangeName("");
    setCryptoAddress("");
    setSelectedToken("");
    setSelectedNetwork("");
    setTokenId(undefined);
    setNetworkId(undefined);
  };

  useEffect(() => {
    if (isOpen) {
      fetchTokenList();
    } else {
      resetForm();
    }
  }, [isOpen]);

  const isFormValid = Boolean(
    exchangeName.trim() &&
      selectedToken &&
      selectedNetwork &&
      cryptoAddress.trim()
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={"Add Exchange"}
      onClose={() => {
        resetForm();
        onClose();
      }}
      onClickCross={() => {
        resetForm();
        onClose();
      }}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="flex flex-col px-[16px] py-[16px] mt-[60px]">
        <div className="flex flex-col gap-4 bg-bg-base rounded-lg p-4 border border-border-subtle">
          <GlobalTextInputField
            placeholder="Enter exchange name"
            value={exchangeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setExchangeName(e.target.value)
            }
            title="Exchange Name"
          />

          {!tokenListLoading && tokenList.length > 0 && (
            <CurrencyLogoDropdown
              listOfValues={tokenList.map((token) => ({
                id: token.currencyId,
                currency: token.currencySymbol,
                currencyLogo: token.currencyImg,
                currencyName: token.currencyName || token.currencySymbol,
                isToken: true,
                isReceive: true,
                key: token.currencyId,
              }))}
              value={tokenId}
              onChange={handleTokenSelection}
              label="Select Token"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              borderColor="border-subtle"
              expandedBorderColor="border-button-primary"
            />
          )}

          <CurrencyLogoDropdown
            listOfValues={
              tokenList
                ?.find((t) => t.currencyId === tokenId)
                ?.networks?.map((network) => ({
                  id: network.networkId,
                  currency: network.networkSymbol,
                  currencyLogo: network.networkImg,
                  isToken: false,
                  standard: network.standard,
                  networkName: network.networkName || network.networkSymbol,
                  isReceive: true,
                  isReceiveNetwork: true,
                  key: network.networkId,
                  estimatedDurationInMins: network.estimatedDurationInMins,
                  confirmations: network.confirmations,
                })) || []
            }
            value={networkId}
            onChange={handleNetworkSelection}
            label="Select Network"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />
          <GlobalTextInputField
            placeholder="Enter crypto address"
            value={cryptoAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCryptoAddress(e.target.value)
            }
            title="Crypto Address"
          />
          <CustomButton
            text="ADD EXCHANGE"
            onClick={handleSubmit}
            isActive={isFormValid && !isLoading}
            isButtonLoading={isLoading}
            className="flex-1 w-full"
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default ExchangeManagementSlider;
